import   React                            ,
       { useCallback                      ,
         useEffect                        ,
         useMemo                          ,
         useState                         }  from "react";

import   styles                              from "./DashboardV2.module.scss";
import   tabsStyles                          from "./DashboardTabs.module.scss";
import   chStyles                            from "./ChannelsSection.module.scss";


import { WWW_URLS                         ,
         WWW_DOMAIN                       }  from '../../../backendServices/urls';
import { getVerifiedMedia                 }  from '../../../backendServices/mediaManagment/api';
import { getVideoById                     ,
         getVideoList                     ,
         retryVideoTranscoding            ,
         setVideoAvailability             }  from '../../../backendServices/mediaManagment/api_video';
import { stopChannel                      ,
         lockChannel                      ,
         unlockChannel                    }  from '../../../backendServices/broadcastingManagment/api_channelModeration';
import   ChannelsSection                   ,
       { DashboardChannel                 ,
         ChannelStats                     ,
         ModerationOutcome                ,
         ChannelKpi                       ,
         stateOf                          }  from './ChannelsSection';
import   VideoCommentsBar                  from './VideoCommentsBar';
import   VideosSection                     ,
       { DashboardVideo                   ,
         VideoStats                       ,
         VideoKpi                         }  from './VideosSection';
import   AssetsSection                     ,
       { DashboardAsset                   ,
         AssetStats                       ,
         AssetKpi                         ,
         HEAVY_BYTES                      ,
         assetSrc                         }  from './AssetsSection';
import   RecordingsSection                 ,
       { DashboardRecording               ,
         RecordingKpi                     ,
         RecordingStats                   }  from './RecordingsSection';
import   TrafficSection                      from './TrafficSection';
import   ChannelDevicesPanel               ,
       { ChannelDevices                   ,
         ChannelDirect                    ,
         deviceLayer                      }  from './ChannelDevicesPanel';
import   EventsSection                       from './EventsSection';
import { getAdminEventContext }              from '../../../backendServices/adminManagment/api_adminEvents';
import { ownerText, size, clock, hour, dayLabel, plural, Segment, creatorPlace, DateRange, todayStart, todayEnd } from './dashboardShared';
import { ActivityTab                      ,
         ActivitySpec                     ,
         Geo                              ,
         Period                           ,
         range                            ,
         textOf                           }  from './ActivityOverview';
import   Box                                 from '../../widget/windows/Box';
import   VideoSheet2                         from '../mediaManagment/VideoSheet2';
import { Video                            }  from '../../../dataTypes/mediaManagment/video_types';
import   VideoPlayer                         from '../../widget/players/VideoPlayer';
import   axios                           from 'axios';
import { API_URLS                         }  from '../../../backendServices/urls';
import { useTypedSelector                 }  from '../../../store/useTypeSelector';
import { RootState                        }  from '../../../store/generalReducer';

// Onglets refaits le 2026-10-17 (Chaînes, puis Vidéos, Assets, Enregistrements et
// Trafic d'après la maquette https://claude.ai/artifact/MtJ8i9W8i9G2uHH3CcnL7N) :
// chaque onglet est son propre composant ; ce fichier relève les données, garde les
// filtres communs (créateur, dates) et les deux fenêtres partagées — lecture d'une
// vidéo ou d'un direct, aperçu d'un fichier.

// Depuis le 2026-10-20, chaque onglet trace un graphe, une carte et des villes de toute
// la période (ActivityOverview.tsx, présentation de l'onglet Trafic) : on relève tout,
// plus seulement les 24 derniers.
const PERIOD_LIMIT   = 1000;
const REFRESH_MS     = 60000;

/** Minuit il y a 6 jours : le début des « 7 jours » de channelStats. */
const isToday = (iso: string | null) => !!iso && new Date(iso).toDateString() === new Date().toDateString();
const inWeek  = (iso: string | null) => !!iso && new Date(iso).getTime() >= weekStart();
const weekStart = () => { const d = new Date(); d.setHours(0, 0, 0, 0); d.setDate(d.getDate() - 6); return d.getTime(); };

type Tab = 'channels' | 'broadcasts' | 'videos' | 'assets' | 'recordings' | 'traffic' | 'events';

/** Ce que la fenêtre de lecture montre : un flux HLS, ou la mire d'une chaîne éteinte. */
type Playing = { title: string; url: string; stopped?: boolean; logo?: string; live?: boolean };

/** Ce que la fenêtre d'aperçu montre : le fichier d'un asset ou d'un enregistrement. */
type Zoom = { title: string; kind: 'image' | 'video' | 'sound'; src: string; facts: [string, string][] };

const ON_STATUSES = [ 'active', 'restricted', 'activating', 'activating_restricted' ];

// Ce que chaque onglet empile dans son graphe et colore sur sa carte.
const CHANNEL_SPEC: ActivitySpec<DashboardChannel> = {
  noun: { one: 'chaîne créée', many: 'chaînes créées' }, title: 'Chaînes créées',
  series: [ { id: 'regie', label: 'Régie web', color: '#2C7DE1' }, { id: 'ios', label: 'iPhone', color: '#F59E0B' },
            { id: 'android', label: 'Android', color: '#A3E635' }, { id: 'other', label: 'Autre', color: '#64748B' } ],
  seriesOf: c => c.origin === 'regie' || c.origin === 'ios' || c.origin === 'android' ? c.origin : 'other',
  dateOf: c => c.creationDate, geoOf: c => c.geo, nameOf: c => c.name,
};
const isDirectLive = (x: ChannelDirect) => !x.endDate && Date.now() - new Date(x.startDate).getTime() < 12 * 3600 * 1000;
// Onglet Diffusion (2026-10-27) : une ligne par diffusion (ChannelBroadcastSession). Pas de position
// propre : la carte prend celle de la chaîne, posée par DashboardV2 (channelGeo).
const DIRECT_SPEC = (geoOfChannel: (id: number) => Geo | null): ActivitySpec<ChannelDirect> => ({
  noun: { one: 'diffusion', many: 'diffusions' }, title: 'Diffusions lancées',
  series: [ { id: 'live', label: 'En cours', color: '#DC2626' }, { id: 'ended', label: 'Terminées', color: '#2C7DE1' } ],
  seriesOf: x => isDirectLive(x) ? 'live' : 'ended',
  dateOf: x => x.startDate, geoOf: x => geoOfChannel(x.channelId), nameOf: x => x.channelName,
});
const VIDEO_SPEC: ActivitySpec<DashboardVideo> = {
  noun: { one: 'vidéo ajoutée', many: 'vidéos ajoutées' }, title: 'Vidéos ajoutées',
  series: [ { id: 'ready', label: 'Visibles', color: '#22C55E' }, { id: 'hidden', label: 'Masquées', color: '#64748B' },
            { id: 'transcoding', label: 'En transcodage', color: '#F59E0B' }, { id: 'failed', label: 'En échec', color: '#DC2626' } ],
  seriesOf: v => v.state, dateOf: v => v.creationDate, geoOf: v => v.geo, nameOf: v => v.name,
};
const ASSET_SPEC: ActivitySpec<DashboardAsset> = {
  noun: { one: 'asset déposé', many: 'assets déposés' }, title: 'Assets déposés',
  series: [ { id: 'image', label: 'Images', color: '#2C7DE1' }, { id: 'video', label: 'Vidéos', color: '#A78BFA' },
            { id: 'sound', label: 'Sons', color: '#A3E635' } ],
  seriesOf: a => a.kind, dateOf: a => a.creationDate, geoOf: a => a.geo, nameOf: a => a.name,
};
const RECORDING_SPEC: ActivitySpec<DashboardRecording> = {
  noun: { one: 'prise', many: 'prises' }, title: 'Enregistrements',
  series: [ { id: 'single', label: 'Programme', color: '#2C7DE1' }, { id: 'multi', label: 'Multi-angle', color: '#A78BFA' },
            { id: 'live', label: 'En cours', color: '#DC2626' } ],
  seriesOf: r => r.recording ? 'live' : r.angles.length ? 'multi' : 'single',
  dateOf: r => r.creationDate, geoOf: r => r.geo, nameOf: r => r.channelName,
};

const DashboardV2: React.FC = () => {

  const { authentication } = useTypedSelector((state: RootState) => state.authenticationReducer);

  const [ videos       , setVideos       ] = useState<DashboardVideo[]>([]);
  const [ videoStats   , setVideoStats   ] = useState<VideoStats | null>(null);
  const [ channelInfos , setChannelInfos ] = useState<DashboardChannel[]>([]);
  const [ channelStats , setChannelStats ] = useState<ChannelStats | null>(null);
  const [ channelDevices, setChannelDevices ] = useState<ChannelDevices[]>([]);
  const [ directs , setDirects ] = useState<ChannelDirect[]>([]);
  const [ assets       , setAssets       ] = useState<DashboardAsset[]>([]);
  const [ assetStats   , setAssetStats   ] = useState<AssetStats | null>(null);
  const [ recordings   , setRecordings   ] = useState<DashboardRecording[]>([]);
  const [ recordingStats, setRecordingStats ] = useState<RecordingStats | null>(null);
  const [ loading  , setLoading  ] = useState<Record<string, boolean>>({});
  const [ errors   , setErrors   ] = useState<Record<string, boolean>>({});
  const [ playing  , setPlaying  ] = useState<Playing | null>(null);
  const [ playError, setPlayError ] = useState('');
  const [ zoom     , setZoom     ] = useState<Zoom | null>(null);
  const [ sheet    , setSheet    ] = useState<{ video: Video | null; error: string; place?: string; ownerId?: number } | null>(null);
  // Un créateur (non superadmin) n'a pas d'onglets : uniquement la vue Diffusion (2026-10-27).
  const [ tab      , setTab      ] = useState<Tab>(authentication.admin?.role === 'ADMIN' ? 'channels' : 'broadcasts');   // créateur : Diffusion et Trafic seulement
  // Période et recherche des onglets Chaînes à Enregistrements, comme l'onglet Trafic.
  // « Aujourd'hui » par défaut, et à chaque retour sur l'onglet Chaînes.
  const [ period   , setPeriod   ] = useState<Period>('today');
  const [ dateFrom , setDateFrom ] = useState<Date>(todayStart);
  const [ dateTo   , setDateTo   ] = useState<Date>(todayEnd);
  const [ query    , setQuery    ] = useState('');
  const [ readAt   , setReadAt   ] = useState<Date | null>(null);

  const needle       = query.trim().toLowerCase();
  const [ kpi, setKpi ] = useState<ChannelKpi>(null);
  const [ directKpi, setDirectKpi ] = useState<'live' | 'ended' | 'channels' | null>(null);
  const [ videoKpi , setVideoKpi  ] = useState<VideoKpi>(null);
  const [ assetKpi , setAssetKpi  ] = useState<AssetKpi>(null);
  const [ recordingKpi, setRecordingKpi ] = useState<RecordingKpi>(null);
  const filtered     = !!needle || kpi !== null || directKpi !== null || videoKpi !== null || assetKpi !== null || recordingKpi !== null;
  const clearFilters = useCallback(() => { setQuery(''); setKpi(null); setDirectKpi(null); setVideoKpi(null); setAssetKpi(null); setRecordingKpi(null); }, []);
  // « Sur 7 jours » demande plus large que la période affichée (aujourd'hui par défaut) : il
  // charge les 7 jours, et les quitter le désactive pour ne pas filtrer une période trop courte.
  const chooseKpi = useCallback((next: ChannelKpi) => {
    if (next === 'week') setPeriod('week');
    else if (kpi === 'week') setPeriod('today');
    setKpi(next);
  }, [kpi]);
  const choosePeriod = useCallback((next: Period) => {
    if (kpi === 'week') setKpi(null);
    setVideoKpi(null);
    setAssetKpi(null);
    setRecordingKpi(null);
    setPeriod(next);
  }, [kpi]);
  // Tuiles Vidéos et Assets : chacune charge la période qu'elle compte ; la quitter revient à aujourd'hui.
  const chooseVideoKpi = useCallback((next: VideoKpi) => {
    setPeriod(next === null || next === 'today' ? 'today' : 'week');
    setVideoKpi(next);
  }, []);
  const chooseRecordingKpi = useCallback((next: RecordingKpi) => {
    if (next === 'space') {   // Compté toutes dates par le serveur.
      setDateFrom(new Date(2020, 0, 1));
      setDateTo(todayEnd());
      setPeriod('custom');
    } else setPeriod(next === 'week' || next === 'live' ? 'week' : 'today');   // Un Rec en cours a pu démarrer avant minuit.
    setRecordingKpi(next);
  }, []);
  const chooseAssetKpi = useCallback((next: AssetKpi) => {
    if (next === 'space' || next === 'heavy') {   // Comptés toutes dates par le serveur.
      setDateFrom(new Date(2020, 0, 1));
      setDateTo(todayEnd());
      setPeriod('custom');
    } else setPeriod(next === 'week' ? 'week' : 'today');
    setAssetKpi(next);
  }, []);
  // Superadmin : tout. Autre compte (créateur) : l'API limite chaque liste à SES chaînes et SES vidéos
  // d'après l'adminId envoyé (2026-10-27), et les actions de modération sont masquées.
  const isAdminRole = authentication.admin?.role === 'ADMIN';
  const myAdminId   = authentication.admin?.id;
  const params = useCallback(() => {
    const [ start, end ] = range(period, dateFrom, dateTo);
    return {
      adminId   : myAdminId,
      limit     : PERIOD_LIMIT,
      videoLimit: PERIOD_LIMIT,
      // L'instant exact (UTC) : « Personnaliser » se règle à la minute près depuis le 2026-10-24.
      from      : start.toISOString(),
      to        : end.toISOString(),
    };
  }, [period, dateFrom, dateTo, myAdminId]);

  // ── Lecture ──
  const playChannel = useCallback((c: { name: string; publicRef: string; channelStatus?: string; hasThumbnail?: boolean; on?: boolean }) => {
    if (!c.publicRef) return;
    setPlayError('');
    const on = c.on ?? ON_STATUSES.includes(c.channelStatus ?? '');
    if (!on) setPlaying({ title: c.name, url: '', stopped: true, logo: c.hasThumbnail ? `${WWW_URLS.MediaServer}${c.publicRef}/images/logo.webp` : '' });
    else     setPlaying({ title: c.name, url: `https://${WWW_DOMAIN}/${c.publicRef}/index.m3u8`, live: true });
  }, []);

  // Vidéo : même adresse que la liste des médias (001).
  const playVideo = useCallback(async (v: DashboardVideo) => {
    if (!v.publicRef) return;
    setPlayError('');
    const url = await getVerifiedMedia([`${WWW_URLS.MediaServer}${v.publicRef}/videos/001/index.m3u8`]);
    if (url) setPlaying({ title: v.name, url });
    else { setPlaying({ title: v.name, url: '' }); setPlayError('Vidéo indisponible (encore en transcodage ?).'); }
  }, []);

  // ── Fiche d'une vidéo : la même que dans Médias, ouverte par-dessus le Dashboard ──
  const openVideoSheet = useCallback(async (v: DashboardVideo) => {
    const place = creatorPlace(v.geo);
    setSheet({ video: null, error: '', place, ownerId: v.ownerId });
    // Le format de la liste, comme dans Médias : GET /api/videos/{id} ne rend ni les catégories
    // ni le statut requis, et la fiche les croyait manquants.
    const one = await getVideoList(false, undefined, v.id);
    // Un serveur sans le filtre `id` rend toute la liste : on y retrouve la vidéo par son id.
    const found = one.code === 1 ? one.payload.find(x => x.id === v.id) : undefined;
    const res = found ? { code: 1, payload: found } as const : await getVideoById(v.id);
    setSheet(res.code === 1 ? { video: res.payload, error: '', place, ownerId: v.ownerId } : { video: null, error: 'Impossible de charger la fiche de cette vidéo.', place });
  }, []);

  // ── Relevés ──
  const loadLatest = useCallback(async () => {
    setLoading(l => ({ ...l, videos: true, channels: true }));
    try {
      const { data } = await axios.get(API_URLS.getDashboardLatest, { params: params() });
      setVideos((data.videos ?? []).map((x: any): DashboardVideo => ({
        id: x.id, ownerId: x.ownerId, featuredUntil: x.featuredUntil ?? null, name: x.name || '', publicRef: x.publicRef || '', hasThumbnail: !!x.hasThumbnail,
        creationDate: x.creationDate ?? null, owner: x.owner || '', ownerEmail: x.ownerEmail || '',
        // Un admin-api d'avant le 2026-10-17 ne rend pas l'état : la vidéo passe pour prête.
        state: x.state || 'ready', progress: x.progress ?? null, durationSeconds: x.durationSeconds ?? null,
        active: x.active ?? true, publicPortal: !!x.publicPortal, views: Number(x.views ?? 0),
        resolutions: x.resolutions || '', social: Array.isArray(x.social) ? x.social : [],
        universe: x.universe ?? null, origin: x.origin || 'regie', sizeBytes: Number(x.sizeBytes ?? 0),
        geo: x.geo ?? null,
      })));
      setVideoStats(data.videoStats ?? null);
      setChannelInfos((data.channels ?? []).map((x: any): DashboardChannel => ({
        id: x.id, name: x.name || '', publicRef: x.publicRef || '', slug: x.slug || '',
        hasThumbnail: !!x.hasThumbnail, creationDate: x.creationDate ?? null, ephemeral: !!x.ephemeral,
        channelStatus: x.channelStatus || '', streamSource: x.streamSource || 'video', publicPortal: !!x.publicPortal,
        origin: x.origin || 'unknown', city: x.city ?? null, universe: x.universe ?? null,
        owner: x.owner || '', ownerEmail: x.ownerEmail || '',
        lock: x.lock ?? null, geo: x.geo ?? null, devices: Array.isArray(x.devices) ? x.devices : [],
        periodReason: x.periodReason ?? 'created', deleted: !!x.deleted, deletedAt: x.deletedAt ?? null, directToday: !!x.directToday,
      })));
      // Un admin-api d'avant le 2026-10-20 ne rend pas les smartphones : aucun.
      setChannelDevices(Array.isArray(data.channelDevices) ? data.channelDevices : []);
      setDirects(Array.isArray(data.directs) ? data.directs : []);
      setChannelStats(data.channelStats ?? null);
      setErrors(e => ({ ...e, videos: false, channels: false }));
      setReadAt(new Date());
    } catch {
      setErrors(e => ({ ...e, videos: true, channels: true }));
    } finally {
      setLoading(l => ({ ...l, videos: false, channels: false }));
    }
  }, [params]);

  // Assets : indépendant des autres listes, une panne de l'un n'abîme pas les autres.
  const loadAssets = useCallback(async () => {
    setLoading(l => ({ ...l, assets: true }));
    try {
      const { data } = await axios.get(API_URLS.getDashboardMediaboardAssets, { params: params() });
      setAssets((data.assets ?? []).map((x: any, i: number): DashboardAsset => ({
        key: `a${x.configId}-${x.filename}-${i}`, kind: x.kind === 'video' || x.kind === 'sound' ? x.kind : 'image',
        name: x.name || x.originalName || x.filename || '', originalName: x.originalName || '',
        configId: x.configId, filename: x.filename || '', channelId: x.channelId ?? 0, channelName: x.channelName || '',
        owner: x.owner || '', ownerEmail: x.ownerEmail || '', creationDate: x.creationDate ?? null,
        sizeBytes: Number(x.sizeBytes ?? 0), format: x.format || '', durationSeconds: x.durationSeconds ?? null,
        place: x.place || 'media', placeLabel: x.placeLabel || '', geo: x.geo ?? null,
      })));
      setAssetStats(data.assetStats ?? null);
      setErrors(e => ({ ...e, assets: false }));
    } catch {
      setErrors(e => ({ ...e, assets: true }));
    } finally {
      setLoading(l => ({ ...l, assets: false }));
    }
  }, [params]);

  const loadRecordings = useCallback(async () => {
    setLoading(l => ({ ...l, recordings: true }));
    try {
      const { data } = await axios.get(API_URLS.getDashboardRecordings, { params: params() });
      setRecordings((data.recordings ?? []).map((x: any): DashboardRecording => ({
        id: x.id, name: x.name || '', format: x.format || 'mp4', channelId: x.channelId ?? 0, channelName: x.channelName || '',
        publicRef: x.publicRef || '', channelOn: !!x.channelOn, owner: x.owner || '', ownerEmail: x.ownerEmail || '',
        durationSeconds: x.durationSeconds ?? null, sizeBytes: x.sizeBytes ?? null, fileUrl: x.fileUrl ?? null,
        creationDate: x.creationDate, stoppedAt: x.stoppedAt ?? null,
        // Un admin-api d'avant le 2026-10-17 ne rend pas stoppedAt : rien n'est « en cours ».
        recording: !!x.recording, elapsedSeconds: x.elapsedSeconds ?? null,
        angles: Array.isArray(x.angles) ? x.angles : [], geo: x.geo ?? null,
      })));
      setRecordingStats(data.recordingStats ?? null);
      setErrors(e => ({ ...e, recordings: false }));
    } catch {
      setErrors(e => ({ ...e, recordings: true }));
    } finally {
      setLoading(l => ({ ...l, recordings: false }));
    }
  }, [params]);

  // Un créateur dont l'identité n'est pas encore connue ne lit rien : sans adminId l'API ne limiterait pas.
  const refreshAll = useCallback(() => { if (!isAdminRole && !myAdminId) return; loadLatest(); if (isAdminRole) { loadAssets(); loadRecordings(); } }, [isAdminRole, myAdminId, loadLatest, loadAssets, loadRecordings]);

  useEffect(() => {
    const all = refreshAll;
    const first = setTimeout(all, 300);   // laisse finir la frappe dans le filtre créateur
    const timer = setInterval(all, REFRESH_MS);
    return () => { clearTimeout(first); clearInterval(timer); };
  }, [refreshAll]);

  // Actions super-admin sur une chaîne (onglet Chaînes) : la liste est relue après coup,
  // pour que l'état (éteinte, verrouillée) vienne du serveur et non d'une supposition.
  const moderation = useCallback(async (run: (adminId: number) => ReturnType<typeof stopChannel>, done: string): Promise<ModerationOutcome> => {
    const adminId = authentication.admin?.id;
    if (!adminId) return { error: 'Compte non identifié : reconnectez-vous.' };
    const res = await run(adminId);
    loadLatest();
    if (!res.ok) return { error: res.error };
    // Verrouillage : dire si le créateur a bien été prévenu.
    const mail = res.state.email;
    const mailText = !mail ? '' : mail.sent ? ` Un e-mail a été envoyé à ${mail.to}.` : ` L'e-mail n'a pas pu être envoyé${mail.to ? ` à ${mail.to}` : ''} : ${mail.error}.`;
    return { error: null, notice: done + mailText };
  }, [authentication.admin?.id, loadLatest]);

  // L'onglet Événements est réservé aux super-admins : le serveur dit si le compte l'est.
  const [ isSuper, setIsSuper ] = useState(false);
  useEffect(() => {
    const id = authentication.admin?.id;
    if (!id) { setIsSuper(false); return; }
    let cancelled = false;
    getAdminEventContext(id).then(r => { if (!cancelled) setIsSuper(r.ok && r.data.superadmin); });
    return () => { cancelled = true; };
  }, [authentication.admin?.id]);

  const retry = useCallback(async (v: DashboardVideo) => {
    const res = await retryVideoTranscoding(v.id, authentication.admin?.id);
    if (res.code === 1) loadLatest();
    return res.code === 1;
  }, [authentication.admin?.id, loadLatest]);

  // Désactive (et retire du portail) ou rétablit une vidéo. `error` vide si tout va bien ; `notice` dit si le créateur a été prévenu.
  const availability = useCallback(async (v: DashboardVideo, enabled: boolean, portal: boolean, comment: string): Promise<{ error: string; notice: string }> => {
    const id = authentication.admin?.id;
    if (!id) return { error: 'Session expirée, reconnectez-vous.', notice: '' };
    const res = await setVideoAvailability(id, v.id, enabled, portal, comment);
    if (res.ok) loadLatest();
    const mail   = res.email;
    const notice = !res.ok ? '' : !mail ? '' : mail.sent ? `Un e-mail a été envoyé au créateur (${mail.to}).` : `L'e-mail au créateur n'a pas pu être envoyé : ${mail.error}.`;
    return { error: res.error, notice };
  }, [authentication.admin?.id, loadLatest]);

  // La recherche porte sur tout l'onglet (chiffres de la liste, graphe, carte, villes, liste).
  const match = useCallback((x: unknown) => !needle || textOf(x).toLowerCase().includes(needle), [needle]);
  const channelsShown   = useMemo(() => channelInfos.filter(match).filter(c =>
    kpi === 'direct'  ? !!c.directToday :
    kpi === 'on'      ? stateOf(c) !== 'off' :
    kpi === 'week'    ? !!c.creationDate && new Date(c.creationDate).getTime() >= weekStart() :
    kpi === 'created' ? !!c.creationDate && new Date(c.creationDate).toDateString() === new Date().toDateString() : true), [channelInfos, match, kpi]);
  const videosShown     = useMemo(() => videos.filter(match).filter(v =>
    videoKpi === 'today'   ? isToday(v.creationDate) :
    videoKpi === 'week'    ? inWeek(v.creationDate) :
    videoKpi === 'working' ? v.state === 'transcoding' :
    videoKpi === 'visible' ? v.state === 'ready' && inWeek(v.creationDate) : true), [videos, match, videoKpi]);
  const assetsShown     = useMemo(() => assets.filter(match).filter(a =>
    assetKpi === 'today' ? isToday(a.creationDate) :
    assetKpi === 'week'  ? inWeek(a.creationDate) :
    assetKpi === 'heavy' ? a.sizeBytes > HEAVY_BYTES : true), [assets, match, assetKpi]);
  const recordingsShown = useMemo(() => recordings.filter(match).filter(r =>
    recordingKpi === 'live'  ? r.recording :
    recordingKpi === 'today' ? isToday(r.creationDate) :
    recordingKpi === 'week'  ? inWeek(r.creationDate) : true), [recordings, match, recordingKpi]);
  const devicesShown    = useMemo(() => channelDevices.filter(match), [channelDevices, match]);
  const directsMatched  = useMemo(() => directs.filter(match), [directs, match]);
  const directsLive     = useMemo(() => directsMatched.filter(isDirectLive), [directsMatched]);
  // Tuile « En cours » ou « Terminées » de l'onglet Diffusion : liste, carte et courbe suivent.
  const directsShown    = useMemo(() => {
    if (directKpi === null) return directsMatched;
    if (directKpi !== 'channels') return directsMatched.filter(x => isDirectLive(x) === (directKpi === 'live'));
    // Une ligne par chaîne : sa diffusion la plus récente.
    const latest = new Map<number, ChannelDirect>();
    directsMatched.forEach(x => {
      const seen = latest.get(x.channelId);
      if (!seen || new Date(x.startDate) > new Date(seen.startDate)) latest.set(x.channelId, x);
    });
    return Array.from(latest.values());
  }, [directsMatched, directKpi]);
  const channelGeo      = useMemo(() => new Map(channelInfos.map(c => [ c.id, c.geo ?? null ] as const)), [channelInfos]);
  const directSpec      = useMemo(() => DIRECT_SPEC(id => (channelGeo.get(id) as Geo | null | undefined) ?? null), [channelGeo]);
  const liveDirects     = directs.filter(isDirectLive).length;
  // Sous une tuile, les smartphones des seules chaînes retenues : sinon la carte garderait les autres.
  const devicesOfDirects = useMemo(() => {
    if (directKpi === null) return devicesShown;
    const ids = new Set(directsShown.map(x => x.channelId));
    return devicesShown.filter(c => ids.has(c.channelId));
  }, [devicesShown, directsShown, directKpi]);
  const devicesLayer    = useMemo(() => deviceLayer(devicesOfDirects, directsShown), [devicesOfDirects, directsShown]);
  const [ shownCount, totalCount ] =
      tab === 'channels'   ? [ channelsShown.length  , channelInfos.length ]
    : tab === 'broadcasts' ? [ directsShown.length   , directs.length ]
    : tab === 'videos'     ? [ videosShown.length    , videos.length ]
    : tab === 'assets'     ? [ assetsShown.length    , assets.length ]
    :                        [ recordingsShown.length, recordings.length ];
  const anyLoading = !!(loading.videos || loading.channels || loading.assets || loading.recordings);
  const anyError   = !!(errors.videos || errors.channels || errors.assets || errors.recordings);
  const activity   = { period, from: dateFrom, to: dateTo, readAt: readAt?.getTime() ?? 0 };

  // Ce que chaque onglet signale dans son titre : ce qui travaille ou attend.
  const working   = videoStats?.states.transcoding ?? 0;
  const liveRec   = recordingStats?.live.length ?? 0;
  const newAssets = assetStats?.today ?? 0;
  const TABS: [Tab, string, React.ReactNode][] = [
    ...(isAdminRole ? [ [ 'channels', 'Chaînes', null ] as [Tab, string, React.ReactNode] ] : []),
    [ 'broadcasts', 'Diffusion'      , liveDirects > 0 ? <span className={`${tabsStyles.tabCount} ${tabsStyles.tabLive}`}>● {liveDirects}</span> : null ],
    ...(isAdminRole ? [
      [ 'videos'    , 'Vidéos'         , working   > 0 ? <span className={`${tabsStyles.tabCount} ${tabsStyles.tabWarn}`}>{working} en cours</span> : null ],
      [ 'assets'    , 'Assets'         , newAssets > 0 ? <span className={tabsStyles.tabCount}>+{newAssets}</span> : null ],
      [ 'recordings', 'Enregistrements', liveRec   > 0 ? <span className={`${tabsStyles.tabCount} ${tabsStyles.tabLive}`}>● {liveRec} REC</span> : null ],
    ] as [Tab, string, React.ReactNode][] : []),
    [ 'traffic'   , 'Trafic'         , null ],
    ...(isSuper ? [ [ 'events', 'Événements', null ] as [Tab, string, React.ReactNode] ] : []),
  ];

  return (
    <div className={styles.dashboardV2}>
      <div className={styles.tabs} role="tablist">
        {TABS.map(([k, label, count]) => (
          <button key={k} role="tab" aria-selected={tab === k}
                  className={`${styles.tab} ${tab === k ? styles.tabActive : ''}`}
                  onClick={() => { setTab(k); setDirectKpi(null); setVideoKpi(null); setAssetKpi(null); setRecordingKpi(null); if (k === 'channels' || k === 'broadcasts') choosePeriod('today'); }}>{label}{count}</button>
        ))}
      </div>

      {/* ── Période et recherche (onglets Chaînes à Enregistrements), comme dans Trafic ── */}
      {tab !== 'traffic' && tab !== 'events' &&
        <div className={chStyles.toolbar} style={{ marginTop: 14 }}>
          <Segment label="Période" value={period} onChange={choosePeriod}
                   choices={[ ['today', "Aujourd'hui"], ['yesterday', 'Hier'], ['week', '7 jours'], ['month', 'Ce mois-ci'], ['custom', 'Personnaliser'] ] as const} />
          {period === 'custom' && <DateRange from={dateFrom} to={dateTo} onFrom={setDateFrom} onTo={setDateTo} />}
          <span style={{ position: 'relative', display: 'inline-flex', alignItems: 'center' }}>
            <input type="text" value={query} onChange={e => setQuery(e.target.value)} aria-label="Rechercher dans tout l'onglet"
                   placeholder="Rechercher : nom, créateur, chaîne, ville…"
                   style={{ minHeight: 34, minWidth: 280, padding: '6px 28px 6px 10px', border: '1px solid #3B5A86', borderRadius: 6, background: '#152B4A', color: '#E6EDF7', font: 'inherit', fontSize: '0.85rem', colorScheme: 'dark' }} />
            {query && <button type="button" onClick={() => setQuery('')} aria-label="Effacer la recherche"
                              style={{ position: 'absolute', right: 2, width: 26, height: 26, border: 'none', borderRadius: '50%', background: 'transparent', color: '#9FB1CC', cursor: 'pointer' }}>✕</button>}
          </span>
          {needle && <span className={chStyles.kpiMuted} aria-live="polite">{plural(shownCount, 'résultat')} sur {totalCount}</span>}
          <span className={chStyles.spacer}/>
          {anyError
            ? <span className={tabsStyles.red}>Relevé impossible</span>
            : readAt && <span className={tabsStyles.liveInd}>{anyLoading ? 'Relevé…' : `Relevé à ${hour(readAt.toISOString())}`}</span>}
          <button type="button" className={chStyles.secondary} onClick={refreshAll} title="Actualiser">↻</button>
        </div>}

      {tab === 'channels' && <ActivityTab items={channelsShown} spec={CHANNEL_SPEC} pickItems {...activity}>{(shown, overview, slice, picked) =>
                             <ChannelsSection channels={shown} stats={channelStats} kpi={kpi} onKpi={chooseKpi} canModerate={isAdminRole}
                                              overview={overview}
                                              picked={picked.items}
                                              loading={!!loading.channels} error={!!errors.channels}
                                              filtered={filtered} onClearFilters={clearFilters}
                                              onPlay={c => playChannel(c)}
                                              onStop={c => moderation(id => stopChannel(c.id, id), `« ${c.name} » est éteinte.`)}
                                              onLock={(c, reason) => moderation(id => lockChannel(c.id, id, reason), `« ${c.name} » est verrouillée.`)}
                                              onUnlock={(c, note) => moderation(id => unlockChannel(c.id, id, note), `« ${c.name} » est déverrouillée.`)} />}
                             </ActivityTab>}

      {tab === 'broadcasts' && <ActivityTab items={directsShown} spec={directSpec} extra={devicesLayer} pickItems {...activity}>{(_shown, overview, slice, picked) =>
                               <>
                                 <div className={chStyles.kpis}>
                                   <button type="button" className={`${chStyles.kpi} ${chStyles.kpiBtn} ${directKpi === null ? chStyles.kpiOn : ''}`} aria-pressed={directKpi === null}
                                           title="Cliquer pour montrer toutes les diffusions de la période" onClick={() => setDirectKpi(null)}>
                                     <span className={chStyles.kpiLabel}>Diffusions</span>
                                     <span className={chStyles.kpiValue}>{directsMatched.length}</span>
                                     <span className={chStyles.kpiMuted}>sur la période</span>
                                   </button>
                                   <button type="button" className={`${chStyles.kpi} ${chStyles.kpiBtn} ${directKpi === 'live' ? chStyles.kpiOn : ''}`} aria-pressed={directKpi === 'live'}
                                           title="Cliquer pour ne montrer que les diffusions en cours (liste, carte, courbe)" onClick={() => setDirectKpi(directKpi === 'live' ? null : 'live')}>
                                     <span className={chStyles.kpiLabel}>En cours</span>
                                     <span className={chStyles.kpiValue}>{directsLive.length}</span>
                                     <span className={directsLive.length > 0 ? chStyles.kpiLive : chStyles.kpiMuted}>{directsLive.length > 0 ? 'à l’antenne' : 'aucune à l’antenne'}</span>
                                   </button>
                                   <button type="button" className={`${chStyles.kpi} ${chStyles.kpiBtn} ${directKpi === 'ended' ? chStyles.kpiOn : ''}`} aria-pressed={directKpi === 'ended'}
                                           title="Cliquer pour ne montrer que les diffusions terminées (liste, carte, courbe)" onClick={() => setDirectKpi(directKpi === 'ended' ? null : 'ended')}>
                                     <span className={chStyles.kpiLabel}>Terminées</span>
                                     <span className={chStyles.kpiValue}>{directsMatched.length - directsLive.length}</span>
                                     <span className={chStyles.kpiMuted}>sur la période</span>
                                   </button>
                                   <button type="button" className={`${chStyles.kpi} ${chStyles.kpiBtn} ${directKpi === 'channels' ? chStyles.kpiOn : ''}`} aria-pressed={directKpi === 'channels'}
                                           title="Cliquer pour ne garder que la dernière diffusion de chaque chaîne (liste, carte, courbe)" onClick={() => setDirectKpi(directKpi === 'channels' ? null : 'channels')}>
                                     <span className={chStyles.kpiLabel}>Chaînes concernées</span>
                                     <span className={chStyles.kpiValue}>{new Set(directsMatched.map(x => x.channelId)).size}</span>
                                     <span className={chStyles.kpiMuted}>sur la période</span>
                                   </button>
                                 </div>
                                 {overview}
                                 <ChannelDevicesPanel list={devicesOfDirects} directs={directsShown} slice={slice} picked={picked} />
                               </>}
                               </ActivityTab>}

      {tab === 'videos' && <ActivityTab items={videosShown} spec={VIDEO_SPEC} pickItems {...activity}>{(shown, overview, _slice, picked) =>
                           <VideosSection onFeatureChanged={loadLatest} videos={shown} stats={videoStats} kpi={videoKpi} onKpi={chooseVideoKpi} overview={overview} picked={picked.items} canModerate={isAdminRole}
                                          loading={!!loading.videos} error={!!errors.videos}
                                          filtered={filtered} onClearFilters={clearFilters}
                                          onPlay={playVideo} onSheet={openVideoSheet} onRetry={retry} onAvailability={availability}
                                          adminId={authentication.admin?.id} />}
                           </ActivityTab>}

      {tab === 'assets' && <ActivityTab items={assetsShown} spec={ASSET_SPEC} pickItems {...activity}>{(shown, overview, _slice, picked) =>
                           <AssetsSection assets={shown} stats={assetStats} kpi={assetKpi} onKpi={chooseAssetKpi} overview={overview} picked={picked.items}
                                          loading={!!loading.assets} error={!!errors.assets}
                                          filtered={filtered} onClearFilters={clearFilters}
                                          onOpen={a => setZoom({ title: a.name, kind: a.kind, src: assetSrc(a), facts: [
                                            [ 'Chaîne', a.channelName ], [ 'Emplacement', a.placeLabel ], [ 'Créateur', ownerText(a.owner, a.ownerEmail) ], [ 'Ville du créateur', creatorPlace(a.geo) ],
                                            [ 'Déposé', `${dayLabel(a.creationDate)} ${hour(a.creationDate)}` ], [ 'Taille', size(a.sizeBytes) ],
                                            [ 'Durée', clock(a.durationSeconds) ], [ 'Format', a.format ],
                                          ] })} />}
                           </ActivityTab>}

      {tab === 'recordings' && <ActivityTab items={recordingsShown} spec={RECORDING_SPEC} pickItems {...activity}>{(shown, overview, _slice, picked) =>
                               <RecordingsSection recordings={shown} stats={recordingStats} kpi={recordingKpi} onKpi={chooseRecordingKpi} overview={overview} picked={picked.items}
                                                  loading={!!loading.recordings} error={!!errors.recordings}
                                                  filtered={filtered} onClearFilters={clearFilters}
                                                  onWatchLive={r => playChannel({ name: r.channelName, publicRef: r.publicRef, on: r.channelOn })}
                                                  onPlayFile={r => r.fileUrl && setZoom({ title: `${r.name}.${r.format}`, kind: 'video', src: r.fileUrl, facts: [
                                                    [ 'Chaîne', r.channelName ], [ 'Créateur', ownerText(r.owner, r.ownerEmail) ], [ 'Ville du créateur', creatorPlace(r.geo) ],
                                                    [ 'Début', `${dayLabel(r.creationDate)} ${hour(r.creationDate)}` ], [ 'Durée', clock(r.durationSeconds) ],
                                                    [ 'Taille', size(r.sizeBytes) ], [ 'Angles', r.angles.length ? String(r.angles.length) : '' ],
                                                  ] })} />}
                               </ActivityTab>}

      {/* Sans le cadre de 520 px de l'ancienne table (loginSection) : l'onglet prend toute la page. */}
      {tab === 'traffic' && <TrafficSection adminId={authentication.admin?.id} creatorMode={!isAdminRole} />}

      {tab === 'events' && isSuper && <EventsSection adminId={authentication.admin?.id} />}

      {zoom && (
        <div className={styles.overlay} onClick={() => setZoom(null)}>
          <div className={styles.modal} onClick={e => e.stopPropagation()}>
            <div className={styles.sectionHeader}>
              <span>{zoom.title}</span>
              <button className={styles.refresh} onClick={() => setZoom(null)} title="Fermer">✕</button>
            </div>
            {zoom.facts.filter(([, v]) => v).map(([k, v]) => <div key={k} className={styles.zoomInfo}>{k} : {v}</div>)}
            {zoom.kind === 'image' && <img className={styles.zoomMedia} src={zoom.src} alt={zoom.title} />}
            {zoom.kind === 'video' && <video className={styles.zoomMedia} src={zoom.src} controls autoPlay playsInline />}
            {zoom.kind === 'sound' && <audio className={styles.zoomAudio} src={zoom.src} controls autoPlay />}
          </div>
        </div>
      )}
      {playing && (
        <div className={styles.overlay} onClick={() => setPlaying(null)}>
          <div className={styles.modal} onClick={e => e.stopPropagation()}>
            <div className={styles.sectionHeader}>
              <span>{playing.live ? 'Direct — ' : ''}{playing.title}</span>
              <button className={styles.refresh} onClick={() => setPlaying(null)} title="Fermer">✕</button>
            </div>
            {playing.url
              ? <VideoPlayer url={playing.url}
                             onError={e => { if (e.fatal) setPlayError(playing.live ? 'Chaîne hors antenne.' : 'Lecture impossible.'); }}
                             onReady={() => setPlayError('')} />
              : playing.stopped
                ? <div className={styles.stopped}>
                    {playing.logo && <img src={playing.logo} alt={playing.title} className={styles.stoppedLogo} />}
                    <div>Est arrêtée</div>
                  </div>
                : null}
            {playError && <div className={styles.empty}>{playError}</div>}
          </div>
        </div>
      )}
      {sheet && (
        <Box width="95vw" height="95vh" onClose={() => setSheet(null)} logo={true}>
          {sheet.video
            ? <>
                {sheet.place && <div className={styles.zoomInfo} title="D'après la dernière connexion du créateur avant le dépôt">Ville du créateur au dépôt : {sheet.place}</div>}
                <VideoCommentsBar videoId={sheet.video.id} adminId={authentication.admin?.id} />
                <VideoSheet2 video={sheet.video} ownerAdminId={sheet.ownerId} onEdit={() => { setSheet(null); loadLatest(); }} />
              </>
            : <div className={styles.empty}>{sheet.error || 'Chargement de la fiche…'}</div>}
        </Box>
      )}
    </div>
  );
};

export default DashboardV2;
